import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useTenantBranding } from '@/branding/TenantBrandingProvider';
import { isRouteTitleOwned, setDocumentTitle } from './document-meta';

/**
 * `<DocumentTitle />` (W5-F1-S4) — keeps the browser tab's title in sync
 * with the partner's `product_name` whenever the route or branding
 * changes.
 *
 * This is the **fallback** title manager mounted in `AppShell`: it sets
 * just `<product_name>` so authenticated routes that don't register
 * their own metadata still show the partner brand in the tab. Pages that
 * want a richer per-route title + `<meta name="description">` + canonical
 * render `<RouteMeta>` (or call `useRouteMeta`) — see `./RouteMeta.tsx`
 * (#1716). While a `<RouteMeta>` is mounted it takes ownership of the
 * title (`isRouteTitleOwned()`), and this fallback yields rather than
 * overwrite the `<page> · <product>` title back to the bare product name.
 *
 * Renders nothing; effect-only. Mount under `<TenantBrandingProvider>`
 * + inside the `<RouterProvider>` so `useLocation` resolves.
 */
export function DocumentTitle(): null {
  const { branding } = useTenantBranding();
  const location = useLocation();

  React.useEffect(() => {
    // Yield to any active <RouteMeta>: it sets a richer "<page> · <product>"
    // title and its effect runs before this one in commit order, so writing
    // the bare product name here would clobber it.
    if (isRouteTitleOwned()) return;
    setDocumentTitle(branding.product_name);
  }, [branding.product_name, location.pathname]);

  return null;
}
